<template>
    <div class="agent-page">
        <!-- 表单内容区域 -->
        <div class="form-container">
            <div class="page-header">
                <h2 class="page-title">{{ type }}</h2>
                <el-button type="primary" icon="el-icon-back" @click="handleBack">返回</el-button>
            </div>
            <el-form ref="tenderForm" :model="formData" :rules="formRules" label-width="150px" class="tender-form">
                <!-- 第一行：项目名称 + 代理机构项目经理 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="项目名称:" prop="projectName" required>
                            <el-input v-model="formData.projectName" placeholder="请输入项目名称"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="代理机构项目经理:" prop="agencyProjectManagerName" required>
                            <el-input v-model="formData.agencyProjectManagerName" placeholder="请输入代理机构项目经理"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <!-- 第二行：项目经理手机号码 + 招标人名称 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="项目经理手机号码:" prop="agencyProjectManagerPhone" required>
                            <el-input v-model="formData.agencyProjectManagerPhone" placeholder="请输入项目经理手机号"
                                @input="formData.agencyProjectManagerPhone = sanitizeMobileInput(formData.agencyProjectManagerPhone)"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <!-- tendereeContactPerson -->
                        <el-form-item label="招标人名称:" prop="tendererContact" required>
                            <el-input v-model="formData.tendererContact" placeholder="请点击选择招标人" readonly
                                @click.native="openTendererDialog" style="cursor: pointer;">
                                <template slot="suffix">
                                    <i class="el-icon-search" style="cursor: pointer;" @click="openTendererDialog"></i>
                                </template>
                            </el-input>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 第三行：招标人联系人 + 联系人手机号码 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="招标人联系人:" prop="tendereeContactPerson" required>
                            <el-input v-model="formData.tendereeContactPerson" placeholder="请点击选择招标人联系人" readonly
                                @click.native="openTendererDialog" style="cursor: pointer;">
                                <template slot="suffix">
                                    <i class="el-icon-search" style="cursor: pointer;" @click="openTendererDialog"></i>
                                </template>
                            </el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="联系人手机号码:" prop="tendereeContactPhone" required>
                            <el-input v-model="formData.tendereeContactPhone" placeholder="请点击选择联系人手机号码" readonly
                                @click.native="openTendererDialog" style="cursor: pointer;">
                                <template slot="suffix">
                                    <i class="el-icon-search" style="cursor: pointer;" @click="openTendererDialog"></i>
                                </template>
                            </el-input>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 第四行:项目所属地区 + 所属行业 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="项目所属地区:" prop="projectRegion" required>
                            <div v-if="showRegionText" class="field-readonly">
                                <span class="field-readonly__text">
                                    {{ [formData.projectRegionProvince, formData.projectRegionCity,
                                    formData.projectRegionDistrict].filter(Boolean).join(' ') || '未填写' }}
                                </span>
                                <el-button type="text" size="small" @click="enableRegionEdit">编辑</el-button>
                            </div>
                            <el-cascader v-else ref="projectRegionCascader" v-model="formData.projectRegion"
                                :props="projectRegionCascaderProps" @change="handleProjectRegionChange"
                                placeholder="请选择项目所属地区" style="width: 100%;" clearable filterable>
                            </el-cascader>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="所属行业:" prop="industry" required>
                            <div v-if="showIndustryText" class="field-readonly">
                                <span class="field-readonly__text">{{ formData.industryName || '未填写' }}</span>
                                <el-button type="text" size="small" @click="enableIndustryEdit">编辑</el-button>
                            </div>
                            <el-cascader v-else ref="mainIndustryCascader" v-model="formData.industry"
                                placeholder="请选择所属行业" :props="industryCascaderProps" @change="handleMainIndustryChange"
                                style="width: 100%;" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 招标范围 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="招标范围:" prop="biddingScope" required>
                            <el-input v-model="formData.biddingScope" type="textarea" :rows="4" placeholder="请输入招标范围"
                                maxlength="2000" show-word-limit>
                            </el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12" v-if="showPurchaseSchemeField">
                        <el-form-item label="采购方案:" prop="schemeCode">
                            <el-input v-model="formData.schemeName" placeholder="请点击选择采购方案" readonly
                                @click.native="openPurchaseSchemeDialog" style="cursor: pointer;">
                                <template slot="suffix">
                                    <i class="el-icon-search" style="cursor: pointer;"
                                        @click="openPurchaseSchemeDialog"></i>
                                </template>
                            </el-input>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 第五行：资金来源 + 采购类型 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="资金来源:" prop="fundSource" required>
                            <el-radio-group v-model="formData.fundSource">
                                <el-radio label="1">政府投资</el-radio>
                                <el-radio label="2">企业自筹</el-radio>
                                <el-radio label="9">其他</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="采购类型:" prop="purchasingOrganizationForm" required>
                            <el-radio-group v-model="formData.purchasingOrganizationForm">
                                <el-radio label="1">货物</el-radio>
                                <el-radio label="2">工程</el-radio>
                                <el-radio label="9">服务</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 第六行：资格审查方式 + 招标组织形式 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="资格审查方式:" prop="credType" required>
                            <el-radio-group v-model="formData.credType">
                                <el-radio label="1">资格预审</el-radio>
                                <el-radio label="0">资格后审</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="招标组织形式:" prop="tenderOrganizeForm" required>
                            <el-radio-group v-model="formData.tenderOrganizeForm">
                                <el-radio label="1">自行招标</el-radio>
                                <el-radio label="2">委托招标</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 第七行：是否交通项目 -->
                <el-row :gutter="24">
                    <el-col :span="12">
                        <el-form-item label="是否交通项目:" prop="isTraffic" required>
                            <el-radio-group v-model="formData.isTraffic">
                                <el-radio label="1">是</el-radio>
                                <el-radio label="0">否</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 推送媒介选择（占满一行） -->
                <el-row>
                    <el-col :span="24">
                        <el-form-item label="推送媒介选择:" prop="pushPlatform">
                            <el-checkbox-group v-model="formData.pushPlatform">
                                <el-checkbox v-for="platform in pushPlatformList" :key="platform.id"
                                    :label="platform.platformCode">
                                    {{ platform.platformName }}
                                </el-checkbox>
                            </el-checkbox-group>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 附件（占满一行） -->
                <el-row>
                    <el-col :span="24">
                        <el-form-item label="附件:" prop="attachments">
                            <div class="attachment-section">
                                <div class="upload-tip">
                                    <span>只能上传类型为
                                        jpg,zip,rar,pdf,doc,xls,docx 的文件</span>
                                </div>
                                <div class="attachment-type">
                                    <el-radio-group v-model="formData.attachmentType">
                                        <el-radio label="委托合同">委托合同</el-radio>
                                        <el-radio label="其他">其他</el-radio>
                                    </el-radio-group>
                                </div>
                                <ChunkUploader ref="chunkUploader" :business="'tender'" :function="'attachment'"
                                    :file-bind-id="formData.fileGuid" :project-code="getProjectCode()"
                                    :accept-types="'.jpg,.zip,.rar,.pdf,.doc,.xls,.docx'"
                                    :max-file-size="1024 * 1024 * 10" :auto-upload="true" @file-added="handleFileAdded"
                                    @upload-success="handleUploadSuccess" @upload-error="handleUploadError"
                                    :fileBindId="fileGuid" @upload-progress="handleUploadProgress" />
                                <div v-if="showAttachmentText" class="attachment-readonly">
                                    <div class="attachment-readonly__header">
                                        <span class="attachment-readonly__title">已上传附件</span>
                                    </div>
                                    <el-table :data="attachmentList" border stripe size="small" style="width: 100%">
                                        <el-table-column type="index" label="序号" width="60"
                                            align="center"></el-table-column>
                                        <el-table-column prop="fileName" label="文件名称" min-width="200"
                                            show-overflow-tooltip></el-table-column>
                                        <el-table-column prop="fileExtension" label="文件类型" width="100" align="center">
                                            <template slot-scope="scope">
                                                <el-tag size="small" type="info">{{ scope.row.fileExtension || '-'
                                                    }}</el-tag>
                                            </template>
                                        </el-table-column>
                                        <el-table-column prop="fileSize" label="文件大小" width="120" align="center">
                                            <template slot-scope="scope">
                                                {{ formatAttachmentFileSize(scope.row.fileSize) }}
                                            </template>
                                        </el-table-column>
                                        <el-table-column label="操作" width="100" align="center">
                                            <template slot-scope="scope">
                                                <el-button type="text" size="small"
                                                    @click="handleAttachmentDownload(scope.row)">
                                                    下载
                                                </el-button>
                                                <el-button type="text" size="small"
                                                    @click="deleteAndDecryptAtt(scope.row)">
                                                    删除
                                                </el-button>
                                            </template>
                                        </el-table-column>
                                    </el-table>
                                </div>

                            </div>
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 操作按钮 -->
                <el-form-item class="form-actions">
                    <el-button type="primary" :loading="loading" @click="handleSubmit(0)">
                        暂存
                    </el-button>
                    <el-button type="primary" :loading="loading" @click="handleSubmit(1)">
                        提交
                    </el-button>
                </el-form-item>
            </el-form>
        </div>

        <!-- 招标人管理弹窗 -->
        <el-dialog title="招标人管理" :visible.sync="tendererDialogVisible" width="55%" :close-on-click-modal="false"
            class="tenderer-dialog">
            <div class="tenderer-management">
                <!-- 搜索区域 -->
                <div class="search-area">
                    <el-form :model="tendererSearchForm" inline>
                        <el-form-item label="招标人名称">
                            <el-input v-model="tendererSearchForm.tendererName" placeholder="请输入招标人名称"
                                style="width: 200px;" clearable @keyup.enter.native="searchTenderer"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="searchTenderer">搜索</el-button>
                            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <!-- 操作按钮 -->
                <div class="operation-buttons">
                    <el-button type="primary" icon="el-icon-plus" @click="openTendererForm()">新增招标人</el-button>
                </div>

                <!-- 招标人列表 -->
                <el-table :data="tendererList" border style="width: 100%; margin-top: 20px;" v-loading="tendererLoading"
                    element-loading-text="加载中...">
                    <el-table-column prop="tendererName" label="招标人名称"></el-table-column>
                    <el-table-column prop="tendereeContactPerson" label="招标联系人"></el-table-column>
                    <el-table-column prop="contactNumber" label="联系电话"></el-table-column>
                    <!-- <el-table-column prop="email" label="邮箱"></el-table-column> -->
                    <el-table-column label="所在地区">
                        <template slot-scope="scope">
                            {{ scope.row.provinceName }} {{ scope.row.cityName }} {{ scope.row.areaName }}
                        </template>
                    </el-table-column>
                    <el-table-column prop="industryName" label="所属行业"></el-table-column>
                    <el-table-column label="操作" width="250" fixed="right">
                        <template slot-scope="scope">
                            <el-button size="mini" @click="selectTenderer(scope.row)">选择</el-button>
                            <el-button size="mini" type="primary" @click="editTenderer(scope.row)">编辑</el-button>
                            <el-button size="mini" type="danger" @click="deleteTenderer(scope.row)">删除</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页组件 -->
                <div class="pagination-wrapper" style="margin-top: 20px; text-align: right;">
                    <el-pagination @size-change="handleTendererSizeChange" @current-change="handleTendererCurrentChange"
                        :current-page="tendererPagination.pageNum" :page-sizes="[10, 20, 50, 100]"
                        :page-size="tendererPagination.pageSize" layout="total, sizes, prev, pager, next, jumper"
                        :total="tendererPagination.total">
                    </el-pagination>
                </div>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="tendererDialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>

        <!-- 新增/编辑招标人弹窗 -->
        <el-dialog :title="tendererFormMode === 'add' ? '新增招标人' : '编辑招标人'" :visible.sync="tendererFormVisible"
            width="60%" :close-on-click-modal="false">
            <el-form ref="tendererForm" :model="tendererFormData" :rules="tendererFormRules" label-width="120px">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="招标人名称" prop="tendererName">
                            <el-input v-model="tendererFormData.tendererName"
                                placeholder="请输入招标人名称"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="联系电话" prop="contactNumber">
                            <el-input v-model="tendererFormData.contactNumber" placeholder="请输入联系电话"
                                @input="tendererFormData.contactNumber = sanitizeMobileInput(tendererFormData.contactNumber)"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="所在地区" prop="region">
                            <el-cascader ref="regionCascader" v-model="tendererFormData.region" placeholder="请选择省市区"
                                :props="regionCascaderProps" @change="handleTendererRegionChange"
                                style="width: 100%;" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="所属行业" prop="industry">
                            <el-cascader ref="industryCascader" v-model="tendererFormData.industry"
                                placeholder="请选择所属行业" :props="industryCascaderProps"
                                @change="handleTendererIndustryChange" style="width: 100%;" />
                        </el-form-item>
                    </el-col>
                </el-row>

                    <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="招标联系人" prop="tendereeContactPerson">
                            <el-input v-model="tendererFormData.tendereeContactPerson" placeholder="请输入招标联系人"></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>




            </el-form>

            <span slot="footer" class="dialog-footer">
                <el-button @click="tendererFormVisible = false">取消</el-button>
                <el-button type="primary" @click="saveTendererForm" :loading="tendererFormLoading">保存</el-button>
            </span>
        </el-dialog>

        <!-- 采购方案选择弹窗 -->
        <el-dialog title="采购方案选择" :visible.sync="purchaseSchemeDialogVisible" width="60%" :close-on-click-modal="false"
            class="purchase-scheme-dialog">
            <div class="purchase-scheme-management">
                <!-- 搜索区域 -->
                <div class="search-area">
                    <el-form :model="purchaseSchemeSearchForm" inline>
                        <el-form-item label="采购方案名称">
                            <el-input v-model="purchaseSchemeSearchForm.schemeName" placeholder="请输入采购方案名称"
                                style="width: 200px;" clearable @keyup.enter.native="searchPurchaseScheme"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="searchPurchaseScheme">搜索</el-button>
                            <el-button icon="el-icon-refresh" @click="resetPurchaseSchemeSearch">重置</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <!-- 采购方案列表 -->
                <el-table :data="purchaseSchemeList" border style="width: 100%; margin-top: 20px;"
                    v-loading="purchaseSchemeLoading" element-loading-text="加载中...">
                    <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
                    <el-table-column prop="schemeName" label="采购方案名称" show-overflow-tooltip></el-table-column>
                    <el-table-column prop="schemeCode" label="方案编号" show-overflow-tooltip></el-table-column>
                    <el-table-column prop="createTime" label="创建时间" width="180"></el-table-column>
                    <el-table-column label="操作" width="100" fixed="right">
                        <template slot-scope="scope">
                            <el-button size="mini" type="primary"
                                @click="selectPurchaseScheme(scope.row)">选择</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <!-- 分页组件 -->
                <div class="pagination-wrapper" style="margin-top: 20px; text-align: right;">
                    <el-pagination @size-change="handlePurchaseSchemeSizeChange"
                        @current-change="handlePurchaseSchemeCurrentChange"
                        :current-page="purchaseSchemePagination.pageNum" :page-sizes="[10, 20, 50, 100]"
                        :page-size="purchaseSchemePagination.pageSize" layout="total, sizes, prev, pager, next, jumper"
                        :total="purchaseSchemePagination.total">
                    </el-pagination>
                </div>
            </div>

            <span slot="footer" class="dialog-footer">
                <el-button @click="purchaseSchemeDialogVisible = false">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { addProject, initProjectData, getTenderList, delTender, saveTender, getProjectDetail, getPurchaseSchemePage } from '@/api/modules/agent'
import { getFileList } from '@/api/modules/base'
import { getPushPlatformList } from '@/api/modules/evaluation'
import ChunkUploader from '@/components/common/ChunkUploader.vue'
import RegionUtils from '@/utils/regionUtils'
import { checkUnifiedSocialCreditCode } from '@/utils/validate'
import { deleteBidAtt } from '@/api/modules/make'

// 项目类型映射配置
const PROJECT_TYPE_MAP = {
    '公开招标新增项目信息': '01',
    '竞争性谈判新增项目信息': '02',
    '竞争性磋商新增项目信息': '03',
    '邀请招标新增项目信息': '04',
    '询比价采购新增项目信息': '05',
    '单一来源新增项目信息': '06'
}

// 表单验证规则
const FORM_RULES = {
    projectName: [
        { required: true, message: '请输入项目名称' },
    ],
    agencyProjectManagerName: [
        { required: true, message: '请输入代理机构项目经理' },
    ],
    agencyProjectManagerPhone: [
        { required: true, message: '请输入项目经理手机号' }
    ],
    tendereeContactPerson: [
        { required: true, message: '请输入招标人联系人' },
    ],
    tendererName: [
        { required: true, message: '请输入招标人名称' },
    ],
    tendererContact: [
        { required: true, message: '请选择招标人联系人' },
    ],
    tendereeContactPhone: [
        { required: true, message: '请输入联系人手机号码' }
    ],
    projectRegion: [
        { required: true, message: '请选择项目所属地区' }
    ],
    industry: [
        { required: true, message: '请选择所属行业' }
    ],
    fundSource: [
        { required: true, message: '请选择资金来源' }
    ],
    purchasingOrganizationForm: [
        { required: true, message: '请选择采购类型' }
    ],
    credType: [
        { required: true, message: '请选择资格审查方式' }
    ],
    tenderOrganizeForm: [
        { required: true, message: '请选择招标组织形式' }
    ],
    isTraffic: [
        { required: true, message: '请选择是否交通项目' }
    ],
    biddingScope: [
        { required: true, message: '请输入招标范围' },
    ]
}
export default {
    name: 'AddTender',
    components: {
        ChunkUploader
    },
    data() {
        return {
            type: '',
            isEditMode: false,
            industryEditMode: false,
            attachmentEditMode: false,
            regionEditMode: false,
            editProjectId: '',
            editProjectCode: '',
            projectData: {},
            pushPlatformList: [],
            fileGuid: '',
            attachmentList: [],
            formData: {
                id: '',
                projectCode: '',
                tenderId: '',
                tendereeName: '',
                tendereeCode: '',
                projectName: '',
                agencyProjectManagerName: '',
                agencyProjectManagerPhone: '',
                tendereeContactPerson: '',
                tendererContact: '',
                tendereeContactPhone: '',
                projectRegion: [],
                industry: [],
                industryCode: '',
                industryName: '',
                fundSource: '1',
                purchasingOrganizationForm: '1',
                credType: '0',
                tenderOrganizeForm: '1',
                isTraffic: '0',
                pushPlatform: [],
                attachmentType: '委托合同',
                schemeCode: '',
                schemeName: '',
                biddingScope: ''
            },
            formRules: FORM_RULES,
            loading: false,

            // 招标人管理相关数据
            tendererDialogVisible: false,
            tendererList: [],
            tendererSearchForm: {
                tendererName: ''
            },
            tendererPagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            tendererLoading: false,

            // 招标人表单相关数据
            tendererFormVisible: false,
            tendererFormMode: 'add', // 'add' 或 'edit'
            tendererFormLoading: false,
            tendererFormData: {
                id: null,
                usccCode: '',
                tendereeContactPerson: '',
                tendererName: '',
                contactNumber: '',
                email: '',
                provinceCode: '',
                provinceName: '',
                cityCode: '',
                cityName: '',
                areaCode: '',
                areaName: '',
                industryCode: '',
                industryName: '',
                region: [],
                industry: []
            },
            tendererFormRules: {
                tendereeContactPerson: [
                    { required: true, message: '请输入招标人名称' }
                ],
                tendererName: [
                    { required: true, message: '请输入招标人单位名称' }
                ],
                usccCode: [
                    {
                        validator: (rule, value, callback) => {
                            const normalizedValue = (value || '')
                                .replace(/[\u200B-\u200D\uFEFF]/g, '')
                                .replace(/\s+/g, '')
                                .toUpperCase()
                            if (!normalizedValue) {
                                callback()
                                return
                            }
                            if (normalizedValue.length !== 18) {
                                callback(new Error('统一社会信用代码应为18位'))
                                return
                            }
                            if (!checkUnifiedSocialCreditCode(normalizedValue)) {
                                callback(new Error('统一社会信用代码格式不正确'))
                                return
                            }
                            callback()
                        },
                        trigger: 'blur'
                    }
                ],
                contactNumber: [
                    { required: true, message: '请输入联系电话' }
                ],
                // email: [
                //     { required: true, message: '请输入邮箱' },
                //     { type: 'email', message: '请输入正确的邮箱格式' }
                // ],
                region: [
                    { required: true, message: '请选择所在地区' }
                ],
                industry: [
                    { required: true, message: '请选择所属行业' }
                ]
            },

            // 采购方案相关数据
            purchaseSchemeDialogVisible: false,
            purchaseSchemeList: [],
            purchaseSchemeSearchForm: {
                schemeName: ''
            },
            purchaseSchemePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            purchaseSchemeLoading: false,
            // 是否有可用的采购方案（招标人选择了且采购方案列表有数据才显示该字段）
            purchaseSchemeAvailable: false
        }
    },
    mounted() {
        this.type = this.$route.query.type || '新增招标'
        this.isEditMode = !!this.$route.query.id
        this.editProjectId = this.$route.query.id || ''
        this.editProjectCode = this.$route.query.projectCode || ''
        this.initProjectData()
        this.getPushPlatformList()

        // 立即生成 fileGuid，确保 ChunkUploader 在任何模式下都有有效的 fileBindId
        this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)

        // 初始化项目所属地区字段
        RegionUtils.initRegionFields(this.formData, {
            provinceField: 'projectRegionProvince',
            provinceCodeField: 'projectRegionProvinceCode',
            cityField: 'projectRegionCity',
            cityCodeField: 'projectRegionCityCode',
            countyField: 'projectRegionDistrict',
            countyCodeField: 'projectRegionDistrictCode'
        })
        if (this.isEditMode && this.editProjectCode) {
            this.loadProjectDetail()
        }
    },
    computed: {

        channelCode() {
            return this.$channelMaterial.getChannelCode()
        },
        // 获取项目类型代码
        projectTypeCode() {
            return PROJECT_TYPE_MAP[this.type] || ''
        },

        // 项目所属地区级联选择器配置
        projectRegionCascaderProps() {
            return RegionUtils.getCascaderProps()
        },

        // 地区级联选择器配置（招标人信息）
        regionCascaderProps() {
            return RegionUtils.getCascaderProps()
        },

        // 行业级联选择器配置
        industryCascaderProps() {
            return RegionUtils.getCascaderProps('COMPANY_INDUSTRY_CODE')
        },

        showIndustryText() {
            return this.isEditMode && !this.industryEditMode && !!this.formData.industryName
        },

        showRegionText() {
            return this.isEditMode && !this.regionEditMode && !!this.formData.projectRegionDistrict
        },

        showAttachmentText() {
            return !this.attachmentEditMode && this.attachmentList.length > 0
        },

        // 所属地区编号（省）
        provinceCode() {
            return this.formData.projectRegionProvinceCode || ''
        },

        // 所属地区编号（市）
        cityCode() {
            return this.formData.projectRegionCityCode || ''
        },

        // 所属地区编号（区县）
        districtCode() {
            return this.formData.projectRegionDistrictCode || ''
        },

        // 所属地区（省）
        provinceName() {
            return this.formData.projectRegionProvince || ''
        },

        // 所属地区（市）
        cityName() {
            return this.formData.projectRegionCity || ''
        },

        // 所属地区（区县）
        districtName() {
            return this.formData.projectRegionDistrict || ''
        },

        // 项目所属地区编号（区县）- 对应后端 projectLocationCode
        projectLocationCode() {
            return this.districtCode
        },

        // 项目所属地区（区县）- 对应后端 projectLocation
        projectLocation() {
            return this.districtName
        },

        // 检查是否有文件正在上传
        hasUploadingFiles() {
            return this.$refs.chunkUploader?.getUploadingFiles()?.length > 0
        },

        // 检查是否有上传失败的文件
        hasFailedFiles() {
            return this.$refs.chunkUploader?.getFailedFiles()?.length > 0
        },

        // 是否显示采购方案字段（招标人已选择 且 该招标人有可用的采购方案）
        showPurchaseSchemeField() {
            return (this.formData.tendereeCode || this.formData.tenderId) && this.purchaseSchemeAvailable
        }
    },
    methods: {

        // 删除附件
        deleteAndDecryptAtt(row) {
            this.$confirm(`确定要删除附件 "${row.fileName}" 吗？`, '删除确认', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const res = await deleteBidAtt(row.fileId)
                    if (res.code === 200) {
                        await this.loadAttachmentList(this.fileGuid)
                    } else {
                        this.$message.error(res.msg || '删除失败')
                    }
                } catch (error) {
                    console.error('删除附件失败:', error)
                    this.$message.error('删除失败：' + (error.message || '未知错误'))
                }
            }).catch(() => {
                // 用户取消删除
            })
        },
        /**
         * 初始化项目数据
         */
        async initProjectData() {
            try {
                const res = await initProjectData()
                if (res.code === 200) {
                    this.projectData = res.data
                    // 不再需要 processCascaderData，使用 RegionUtils 懒加载
                } else {
                    this.$message.error(res.msg || '初始化项目数据失败')
                }
            } catch (error) {
                console.error('初始化项目数据失败:', error)
                this.$message.error('初始化项目数据失败')
            }
        },
        async loadProjectDetail() {
            try {
                const res = await getProjectDetail({ projectCode: this.editProjectCode })
                if (res.code === 200) {
                    this.fillFormData(res.data || {})
                    // 兜底：如果后端返回了有效的 fileGuid 则使用，否则保留 mounted 中生成的
                    this.fileGuid = res.data.fileGuid || this.fileGuid

                    await this.loadAttachmentList(this.fileGuid)
                    // 编辑模式下检查采购方案可用性
                    if (this.formData.tendereeCode) {
                        await this.checkPurchaseSchemeAvailability()
                    }
                } else {
                    this.$message.error(res.msg || '获取项目详情失败')
                }
            } catch (error) {
                console.error('获取项目详情失败:', error)
                this.$message.error('获取项目详情失败')
            }
        },
        async loadAttachmentList() {
            if (!this.fileGuid) {
                this.attachmentList = []
                return
            }
            try {
                const res = await getFileList(this.fileGuid)
                if (res.code === 200 && Array.isArray(res.data)) {
                    this.attachmentList = res.data
                } else {
                    this.attachmentList = []
                }
            } catch (error) {
                console.error('获取附件列表失败:', error)
                this.attachmentList = []
            }
        },
        fillFormData(detail) {
            const projectRegion = [
                detail.projectLocationProvinceCode,
                detail.projectLocationCityCode,
                detail.projectLocationCode
            ].filter(Boolean)

            const industry = Array.isArray(detail.industry)
                ? detail.industry
                : (detail.industry
                    ? String(detail.industry).split(',').filter(Boolean)
                    : [detail.industryCode].filter(Boolean))

            const pushPlatform = Array.isArray(detail.pushPlatform)
                ? detail.pushPlatform
                : (detail.pushPlatform ? String(detail.pushPlatform).split(',').filter(Boolean) : [])

            this.fileGuid = detail.fileGuid || this.fileGuid
            this.formData = {
                ...this.formData,
                ...detail,
                id: detail.id || this.editProjectId,
                projectCode: detail.projectCode || this.editProjectCode,
                tenderId: detail.tenderId || detail.tendereeId || this.formData.tenderId,
                tenderee_name: detail.tenderee_name || detail.tendereeName || this.formData.tenderee_name,
                tenderee_code: detail.tenderee_code || detail.tendereeCode || this.formData.tenderee_code,
                projectName: detail.projectName || '',
                agencyProjectManagerName: detail.agencyProjectManagerName || '',
                agencyProjectManagerPhone: detail.agencyProjectManagerPhone || '',
                tendereeContactPerson: detail.tendereeContactPerson || detail.tendereeName || '',
                tendererContact: detail.tendereeName,
                tendereeContactPhone: detail.tendereeContactPhone || detail.contactNumber || '',
                projectRegion,
                industry,
                fundSource: detail.fundSource != null ? String(detail.fundSource) : this.formData.fundSource,
                purchasingOrganizationForm: detail.purchasingOrganizationForm != null ? String(detail.purchasingOrganizationForm) : this.formData.purchasingOrganizationForm,
                credType: detail.credType != null ? String(detail.credType) : this.formData.credType,
                tenderOrganizeForm: detail.tenderOrganizeForm != null ? String(detail.tenderOrganizeForm) : this.formData.tenderOrganizeForm,
                isTraffic: detail.isTraffic != null ? String(detail.isTraffic) : this.formData.isTraffic,
                pushPlatform,
                attachmentType: detail.attachmentType || this.formData.attachmentType,
                fileGuid: detail.fileGuid || this.fileGuid,
                projectRegionProvince: detail.projectLocationProvince || this.formData.projectRegionProvince,
                projectRegionProvinceCode: detail.projectLocationProvinceCode || this.formData.projectRegionProvinceCode,
                projectRegionCity: detail.projectLocationCity || this.formData.projectRegionCity,
                projectRegionCityCode: detail.projectLocationCityCode || this.formData.projectRegionCityCode,
                projectRegionDistrict: detail.projectLocation || this.formData.projectRegionDistrict,
                projectRegionDistrictCode: detail.projectLocationCode || this.formData.projectRegionDistrictCode,
                industryName: detail.industryName || this.formData.industryName,
                industryCode: detail.industryCode || this.formData.industryCode,
            }
            // 回填后，如果地区有值，在编辑模式下默认显示文字（避免级联加载问题）
            if (this.isEditMode && (this.formData.projectRegionProvince || this.formData.projectRegionCity || this.formData.projectRegionDistrict)) {
                this.regionEditMode = false
            }
        },
        //  获取当前招标代理维护的所有招标人
        async getTenderList() {
            try {
                this.tendererLoading = true
                const params = {
                    pageNum: this.tendererPagination.pageNum,
                    pageSize: this.tendererPagination.pageSize,
                    tendererName: this.tendererSearchForm.tendererName || ''
                }
                const res = await getTenderList(params)
                if (res.code === 200) {
                    const data = res.data
                    if (data && typeof data === 'object') {
                        // 分页数据结构
                        this.tendererList = Array.isArray(data.records) ? data.records :
                            Array.isArray(data.list) ? data.list :
                                Array.isArray(data.rows) ? data.rows : []
                        this.tendererPagination.total = data.total || 0
                    } else {
                        // 直接数组结构
                        this.tendererList = Array.isArray(data) ? data : []
                        this.tendererPagination.total = this.tendererList.length
                    }
                } else {
                    this.$message.error(res.msg || '获取招标人列表失败')
                }
            } catch (error) {
                console.error('获取招标人列表失败:', error)
                this.$message.error('获取招标人列表失败')
            } finally {
                this.tendererLoading = false
            }
        },

        // 打开招标人管理弹窗
        async openTendererDialog() {
            // 重置分页和搜索
            this.tendererPagination.pageNum = 1
            this.tendererSearchForm.tendererName = ''
            await this.getTenderList()
            this.tendererDialogVisible = true
        },

        // 搜索招标人
        searchTenderer() {
            this.tendererPagination.pageNum = 1
            this.getTenderList()
        },

        // 重置搜索
        resetSearch() {
            this.tendererSearchForm.tendererName = ''
            this.tendererPagination.pageNum = 1
            this.getTenderList()
        },

        // 分页大小改变
        handleTendererSizeChange(val) {
            this.tendererPagination.pageSize = val
            this.tendererPagination.pageNum = 1
            this.getTenderList()
        },

        // 当前页改变
        handleTendererCurrentChange(val) {
            this.tendererPagination.pageNum = val
            this.getTenderList()
        },

        sanitizeMobileInput(value) {
            return (value || '').replace(/\D/g, '')
        },

        getIndustryPathInfo(cascaderRef, fallbackValue = [], fallbackName = '') {
            const checkedNodes = cascaderRef?.get·CheckedNodes?.() || []
            const pathNodes = checkedNodes[0]?.pathNodes || []
            const codes = pathNodes.map(node => node.value).filter(Boolean)
            const names = pathNodes.map(node => node.label).filter(Boolean)

            return {
                industry: codes.length ? codes : (Array.isArray(fallbackValue) ? fallbackValue.filter(Boolean) : []),
                industryCode: codes.length ? codes.join(',') : ((Array.isArray(fallbackValue) ? fallbackValue.filter(Boolean) : []).join(',')),
                industryName: names.length ? names.join('/') : (fallbackName || '')
            }
        },

        // 选择招标人
        async selectTenderer(tenderer) {
            this.formData.tenderId = tenderer.id
            this.formData.tendereeName = tenderer.tendererName
            this.formData.tendereeCode = tenderer.tendererCode
            this.formData.usccCode = tenderer.usccCode
            this.formData.tendereeContactPerson = tenderer.tendereeContactPerson
            this.formData.tendererContact = tenderer.tendererName
            this.formData.tendereeContactPhone = this.sanitizeMobileInput(tenderer.contactNumber)
            // 清空之前选择的采购方案
            this.formData.schemeCode = ''
            this.formData.schemeName = ''
            this.tendererDialogVisible = false
            this.$message.success('已选择招标人：' + (tenderer.tendereeContactPerson || tenderer.tendererName))
            // 检查该招标人是否有可用的采购方案
            await this.checkPurchaseSchemeAvailability()
        },

        /**
         * 检查当前招标人是否有可用的采购方案
         */
        async checkPurchaseSchemeAvailability() {
            if (!this.formData.tendereeCode) {
                this.purchaseSchemeAvailable = false
                return
            }
            try {
                const params = {
                    pageNum: 1,
                    pageSize: 1,
                    tendererCode: this.formData.tendereeCode
                }
                const res = await getPurchaseSchemePage(params)
                if (res.code === 200) {
                    const data = res.data
                    const total = (data && typeof data === 'object') ? (data.total || 0) : 0
                    this.purchaseSchemeAvailable = total > 0
                } else {
                    this.purchaseSchemeAvailable = false
                }
            } catch (error) {
                console.error('检查采购方案可用性失败:', error)
                this.purchaseSchemeAvailable = false
            }
        },

        // 打开新增招标人表单f
        openTendererForm(tenderer = null) {
            if (tenderer) {
                // 编辑模式
                this.tendererFormMode = 'edit'
                this.tendererFormData = {
                    id: tenderer.id,
                    tendererContact: tenderer.tendererName || '',
                    usccCode: (tenderer.usccCode || '').replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '').toUpperCase(),
                    tendereeContactPerson: tenderer.tendereeContactPerson || '',
                    tendererName: tenderer.tendererName || '',
                    contactNumber: this.sanitizeMobileInput(tenderer.contactNumber),
                    email: tenderer.email || '',
                    provinceCode: tenderer.provinceCode || '',
                    provinceName: tenderer.provinceName || '',
                    cityCode: tenderer.cityCode || '',
                    cityName: tenderer.cityName || '',
                    areaCode: tenderer.areaCode || '',
                    areaName: tenderer.areaName || '',
                    industryCode: tenderer.industryCode || '',
                    industryName: tenderer.industryName || '',
                    region: [tenderer.provinceCode, tenderer.cityCode, tenderer.areaCode].filter(Boolean),
                    industry: Array.isArray(tenderer.industry)
                        ? tenderer.industry.filter(Boolean)
                        : (tenderer.industryCode ? String(tenderer.industryCode).split(',').filter(Boolean) : [])
                }
            } else {
                // 新增模式
                this.tendererFormMode = 'add'
                this.tendererFormData = {
                    id: null,
                    usccCode: '',
                    tendereeContactPerson: '',
                    tendererName: '',
                    contactNumber: '',
                    email: '',
                    provinceCode: '',
                    provinceName: '',
                    cityCode: '',
                    cityName: '',
                    areaCode: '',
                    areaName: '',
                    industryCode: '',
                    industryName: '',
                    region: [],
                    industry: []
                }
            }
            this.tendererFormVisible = true
        },

        // 编辑招标人
        editTenderer(tenderer) {
            this.openTendererForm(tenderer)
        },

        // 删除招标人
        async deleteTenderer(tenderer) {
            try {
                await this.$confirm('确定要删除该招标人吗？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                })

                const res = await delTender(tenderer.id)
                if (res.code === 200) {
                    this.$message.success('删除成功')
                    await this.getTenderList()
                    if (this.formData.tendereeContactPerson == tenderer.tendereeContactPerson && this.formData.tendererContact === tenderer.tendererName) {
                        this.formData.tendereeContactPerson = ""
                        this.formData.tendererContact = ""
                        this.formData.tendereeContactPhone = ""
                        this.formData.tendereeCode = ""
                        this.formData.tenderId = ""
                        this.purchaseSchemeAvailable = false
                    }
                } else {
                    this.$message.error(res.msg || '删除失败')
                }
            } catch (error) {
                if (error !== 'cancel') {
                    console.error('删除招标人失败:', error)
                    this.$message.error('删除失败')
                }
            }
        },

        // 招标人地区选择变化处理
        async handleTendererRegionChange(value) {
            await RegionUtils.handleRegionChange(
                value,
                this.$refs.regionCascader,
                this.tendererFormData,
                {
                    provinceField: 'provinceName',
                    provinceCodeField: 'provinceCode',
                    cityField: 'cityName',
                    cityCodeField: 'cityCode',
                    countyField: 'areaName',
                    countyCodeField: 'areaCode',
                    regionField: 'region',
                    enableDebug: false
                },
                this,
                'REGION_CODE'
            )
        },

        // 主表单行业选择变化处理
        async handleMainIndustryChange(value) {
            await RegionUtils.handleRegionChange(
                value,
                this.$refs.mainIndustryCascader,
                this.formData,
                {
                    provinceField: '',
                    provinceCodeField: '',
                    cityField: '',
                    cityCodeField: '',
                    countyField: 'industryName',
                    countyCodeField: 'industryCode',
                    regionField: 'industry',
                    enableDebug: false
                },
                this,
                'COMPANY_INDUSTRY_CODE'
            )

            const checkedNodes = this.$refs.mainIndustryCascader?.getCheckedNodes?.() || []
            const pathNodes = checkedNodes[0]?.pathNodes || []
            const lastNode = pathNodes[pathNodes.length - 1] || {}

            this.formData.industryCode = lastNode.value || value[value.length - 1] || ''
            this.formData.industryName = lastNode.label || ''
            this.industryEditMode = true
        },

        enableIndustryEdit() {
            this.industryEditMode = true
        },

        enableRegionEdit() {
            this.regionEditMode = true
        },

        enableAttachmentEdit() {
            this.fileGuid = new Date().getTime() + Math.random().toString().substr(2, 5)
            this.attachmentEditMode = true
        },

        formatAttachmentFileSize(bytes) {
            if (!bytes || bytes === 0) return '-'
            const k = 1024
            const sizes = ['B', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
        },

        handleAttachmentDownload(file) {
            if (file.fileUrl) {
                window.open(file.fileUrl, '_blank')
            } else {
                this.$message.warning('文件下载链接不存在')
            }
        },

        // 招标人行业选择变化处理
        async handleTendererIndustryChange(value) {
            await RegionUtils.handleRegionChange(
                value,
                this.$refs.industryCascader,
                this.tendererFormData,
                {
                    provinceField: '',
                    provinceCodeField: '',
                    cityField: '',
                    cityCodeField: '',
                    countyField: 'industryName',
                    countyCodeField: 'industryCode',
                    regionField: 'industry',
                    enableDebug: false
                },
                this,
                'BID_SECTION_CLASSIFY_CODE'
            )

            const industryPathInfo = this.getIndustryPathInfo(
                this.$refs.industryCascader,
                value,
                this.tendererFormData.industryName
            )

            this.tendererFormData.industry = industryPathInfo.industry
            this.tendererFormData.industryCode = industryPathInfo.industryCode
            this.tendererFormData.industryName = industryPathInfo.industryName
        },

        // 保存招标人表单
        async saveTendererForm() {
            try {
                this.tendererFormData.usccCode = (this.tendererFormData.usccCode || '')
                    .replace(/[\u200B-\u200D\uFEFF]/g, '')
                    .replace(/\s+/g, '')
                    .toUpperCase()
                await this.$refs.tendererForm.validate()

                if (Array.isArray(this.tendererFormData.region) && this.tendererFormData.region.length > 0
                    && (!this.tendererFormData.areaCode || !this.tendererFormData.areaName)) {
                    const checkedNodes = this.$refs.regionCascader?.getCheckedNodes?.() || []
                    const pathNodes = checkedNodes[0]?.pathNodes || []
                    const provinceNode = pathNodes[0] || {}
                    const cityNode = pathNodes[1] || {}
                    const areaNode = pathNodes[2] || {}
                    this.tendererFormData.provinceCode = provinceNode.value || this.tendererFormData.region[0] || ''
                    this.tendererFormData.provinceName = provinceNode.label || this.tendererFormData.provinceName || ''
                    this.tendererFormData.cityCode = cityNode.value || this.tendererFormData.region[1] || ''
                    this.tendererFormData.cityName = cityNode.label || this.tendererFormData.cityName || ''
                    this.tendererFormData.areaCode = areaNode.value || this.tendererFormData.region[2] || ''
                    this.tendererFormData.areaName = areaNode.label || this.tendererFormData.areaName || ''
                }

                // 兜底：若已选择行业但编码/名称未正确回填，则在保存前再次解析
                if (Array.isArray(this.tendererFormData.industry) && this.tendererFormData.industry.length > 0
                    && (!this.tendererFormData.industryCode || !this.tendererFormData.industryName)) {
                    const industryPathInfo = this.getIndustryPathInfo(
                        this.$refs.industryCascader,
                        this.tendererFormData.industry,
                        this.tendererFormData.industryName
                    )
                    this.tendererFormData.industry = industryPathInfo.industry
                    this.tendererFormData.industryCode = industryPathInfo.industryCode
                    this.tendererFormData.industryName = industryPathInfo.industryName
                }

                this.tendererFormLoading = true

                const submitData = {
                    tendereeContactPerson: this.tendererFormData.tendereeContactPerson,
                    tendererName: this.tendererFormData.tendererName,
                    usccCode: this.tendererFormData.usccCode,
                    contactNumber: this.tendererFormData.contactNumber,
                    email: this.tendererFormData.email,
                    provinceCode: this.tendererFormData.provinceCode,
                    provinceName: this.tendererFormData.provinceName,
                    cityCode: this.tendererFormData.cityCode,
                    cityName: this.tendererFormData.cityName,
                    areaCode: this.tendererFormData.areaCode,
                    areaName: this.tendererFormData.areaName,
                    industryCode: this.tendererFormData.industryCode,
                    industryName: this.tendererFormData.industryName
                }

                // 编辑时需要传递 id 和 usccCode
                if (this.tendererFormMode === 'edit') {
                    submitData.id = this.tendererFormData.id
                    submitData.usccCode = this.tendererFormData.usccCode
                }

                const res = await saveTender(submitData)
                if (res.code === 200) {
                    this.$message.success(this.tendererFormMode === 'add' ? '新增成功' : '编辑成功')
                    this.tendererFormVisible = false
                    await this.getTenderList()
                } else {
                    this.$message.error(res.msg || '保存失败')
                }
            } catch (error) {
                if (error !== false) { // 表单验证失败时不显示错误消息
                    console.error('保存招标人失败:', error)
                    this.$message.error('保存失败')
                }
            } finally {
                this.tendererFormLoading = false
            }
        },

        /**
         * 项目所属地区选择变化处理
         */
        async handleProjectRegionChange(value) {
            await RegionUtils.handleRegionChange(
                value,
                this.$refs.projectRegionCascader,
                this.formData,
                {
                    provinceField: 'projectRegionProvince',
                    provinceCodeField: 'projectRegionProvinceCode',
                    cityField: 'projectRegionCity',
                    cityCodeField: 'projectRegionCityCode',
                    countyField: 'projectRegionDistrict',
                    countyCodeField: 'projectRegionDistrictCode'
                }
            )
        },

        /**
         * 生成项目代码
         */
        generateProjectCode() {
            const projectTypeCode = this.projectTypeCode
            if (!projectTypeCode) {
                throw new Error('无效的项目类型')
            }

            const timestamp = Date.now().toString().slice(-8)
            const random = Math.random().toString(36).substr(2, 4)
            return `${projectTypeCode}${timestamp}${random}`.toUpperCase()
        },

        /**
         * 获取项目代码（兼容性方法）
         */
        getProjectCode() {
            return this.formData.projectCode || this.editProjectCode || this.generateProjectCode()
        },

        /**
         * 返回上一页
         */
        handleBack() {
            this.$router.go(-1)
        },

        /**
         * 验证文件上传状态
         */
        validateFileUpload() {
            if (this.hasUploadingFiles) {
                this.$message.warning('有文件正在上传中，请等待上传完成后再提交')
                return false
            }

            if (this.hasFailedFiles) {
                this.$message.warning('有文件上传失败，请重新上传或删除后提交')
                return false
            }

            return true
        },

        /**
         * 提交表单
         */
        async handleSubmit(status) {
            try {
                const isValid = await this.$refs.tenderForm.validate()
                if (!isValid) {
                    this.$message.error('请检查表单填写是否完整')
                    return false
                }

                if (!this.validateFileUpload()) {
                    return false
                }

                await this.addProject(status)
            } catch (error) {
                console.error('表单提交失败:', error)
                this.$message.error('表单提交失败，请重试')
            }
        },

        /**
         * 处理文件附件信息
         */
        processFileAttachments() {
            const completedFiles = this.$refs.chunkUploader?.getCompletedFiles() || []
            return completedFiles.map(file => ({
                fileName: file.fileName,
                fileType: file.fileType,
                fileUrl: file.fileUrl,
                fileId: file.fileId,
                attachmentType: this.formData.attachmentType,
                uploadTime: file.uploadTime
            }))
        },

        /**
         * 构建提交数据
         */
        buildSubmitData(status) {
            return {
                ...this.formData,
                projectCode: this.getProjectCode(),
                industryCode: this.formData.industryCode,
                industryName: this.formData.industryName,
                projectRegion: Array.isArray(this.formData.projectRegion)
                    ? this.formData.projectRegion.join(',')
                    : '',
                pushPlatform: Array.isArray(this.formData.pushPlatform)
                    ? this.formData.pushPlatform.join(',')
                    : '',
                projectLocationProvinceCode: this.provinceCode,
                projectLocationCityCode: this.cityCode,
                projectLocationCode: this.districtCode,
                projectLocationProvince: this.provinceName,
                projectLocationCity: this.cityName,
                projectLocation: this.districtName,
                status,
                projectType: this.projectTypeCode,
                attachments: this.processFileAttachments(),
                procurementType: this.$route.query.procurementType,
            }
        },

        /**
         * 提交项目
         */
        async addProject(status) {
            if (this.loading) {
                this.$message.warning('正在提交中，请勿重复操作')
                return
            }
            this.loading = true
            const data = this.buildSubmitData(status)
            data.fileGuid = this.fileGuid
            // data.tendereeName = data.tendereeContactPerson


            try {
                const submitData = data
                submitData.channelCode = this.channelCode

                // 暂时删除这些字段
                delete submitData.industry;
                const res = await addProject(submitData)
                if (res.code === 200) {
                    this.$message.success(status === 1 ? '项目提交成功' : '项目暂存成功')
                    this.handleBack()
                } else {
                    this.$message.error(res.msg || '项目提交失败')
                }
            } catch (error) {
                console.error('项目提交失败:', error)
                this.$message.error('项目提交失败，请重试')
            } finally {
                this.loading = false
            }
        },

        /**
         * 重置表单
         */
        handleReset() {
            this.$refs.tenderForm?.resetFields()
            this.$refs.chunkUploader?.reset()
        },

        /**
         * 文件添加事件
         */
        handleFileAdded(fileInfo) {
            console.log('文件已添加:', fileInfo)
        },

        /**
         * 上传成功事件
         */
        handleUploadSuccess(data) {
            console.log('文件上传成功:', data)
            this.$message.success(`文件 ${data.fileInfo.fileName} 上传成功`)
        },

        /**
         * 上传失败事件
         */
        handleUploadError(data) {
            console.error('文件上传失败:', data)
            const errorMessage = data.error?.message || '未知错误'
            this.$message.error(`文件 ${data.fileInfo.fileName} 上传失败: ${errorMessage}`)
        },

        /**
         * 文件删除事件
         */
        handleFileRemoved(fileInfo) {
            console.log('文件已删除:', fileInfo)
        },
        /**
         * 获取推送平台列表
         */
        getPushPlatformList() {
            getPushPlatformList().then(res => {
                if (res.code === 200) {
                    this.pushPlatformList = res.data || []
                    console.log('--------获取推送平台列表', res.data);

                    const hasSelectedPushPlatform = Array.isArray(this.formData.pushPlatform) && this.formData.pushPlatform.length > 0
                    const defaultPlatform = this.pushPlatformList.find(platform => String(platform.platformCode) === '02')
                    if (!hasSelectedPushPlatform && defaultPlatform) {
                        this.formData.pushPlatform = [String(defaultPlatform.platformCode)]
                    }

                } else {
                    this.$message.error(res.msg || '获取推送平台列表失败')
                }
            }).catch(err => {
                console.error('获取推送平台列表失败:', err)
                this.$message.error('获取推送平台列表失败')
            })
        },

        /**
         * 打开采购方案选择弹窗
         */
        async openPurchaseSchemeDialog() {
            if (!this.formData.tenderId || !this.formData.tendereeCode) {
                this.$message.warning('请先选择招标人')
                return
            }
            // 重置分页和搜索
            this.purchaseSchemePagination.pageNum = 1
            this.purchaseSchemeSearchForm.schemeName = ''
            await this.getPurchaseSchemeList()
            this.purchaseSchemeDialogVisible = true
        },

        /**
         * 获取采购方案列表
         */
        async getPurchaseSchemeList() {
            try {
                this.purchaseSchemeLoading = true
                const params = {
                    pageNum: this.purchaseSchemePagination.pageNum,
                    pageSize: this.purchaseSchemePagination.pageSize,
                    schemeName: this.purchaseSchemeSearchForm.schemeName || '',
                    tendererCode: this.formData.tendereeCode
                }
                const res = await getPurchaseSchemePage(params)
                if (res.code === 200) {
                    const data = res.data
                    if (data && typeof data === 'object') {
                        // 分页数据结构
                        this.purchaseSchemeList = Array.isArray(data.records) ? data.records :
                            Array.isArray(data.list) ? data.list :
                                Array.isArray(data.rows) ? data.rows : []
                        this.purchaseSchemePagination.total = data.total || 0
                    } else {
                        // 直接数组结构
                        this.purchaseSchemeList = Array.isArray(data) ? data : []
                        this.purchaseSchemePagination.total = this.purchaseSchemeList.length
                    }
                } else {
                    this.$message.error(res.msg || '获取采购方案列表失败')
                }
            } catch (error) {
                console.error('获取采购方案列表失败:', error)
                this.$message.error('获取采购方案列表失败')
            } finally {
                this.purchaseSchemeLoading = false
            }
        },

        /**
         * 搜索采购方案
         */
        searchPurchaseScheme() {
            this.purchaseSchemePagination.pageNum = 1
            this.getPurchaseSchemeList()
        },

        /**
         * 重置采购方案搜索
         */
        resetPurchaseSchemeSearch() {
            this.purchaseSchemeSearchForm.schemeName = ''
            this.purchaseSchemePagination.pageNum = 1
            this.getPurchaseSchemeList()
        },

        /**
         * 采购方案分页大小改变
         */
        handlePurchaseSchemeSizeChange(val) {
            this.purchaseSchemePagination.pageSize = val
            this.purchaseSchemePagination.pageNum = 1
            this.getPurchaseSchemeList()
        },

        /**
         * 采购方案当前页改变
         */
        handlePurchaseSchemeCurrentChange(val) {
            this.purchaseSchemePagination.pageNum = val
            this.getPurchaseSchemeList()
        },

        /**
         * 选择采购方案
         */
        selectPurchaseScheme(scheme) {
            this.formData.schemeCode = scheme.schemeCode
            this.formData.schemeName = scheme.schemeName
            this.purchaseSchemeDialogVisible = false
            this.$message.success('已选择采购方案：' + scheme.schemeName)
        }
    }
}
</script>

<style lang="less" scoped>
.agent-page {
    padding: 0;
    background-color: transparent;
    min-height: calc(100vh - 64px);

    .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 20px;

        .page-title {
            font-size: 22px;
            font-weight: 600;
            color: #303133;
            margin: 0;
            position: relative;
            padding-left: 12px;

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 4px;
                height: 20px;
                background: #409EFF;
                border-radius: 2px;
            }
        }

        /deep/ .el-button {
            border-radius: 4px;
            font-weight: 500;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
            }

            &:active {
                transform: translateY(0);
            }
        }
    }
}

.form-container {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    padding: 32px 40px;
    max-height: calc(100vh - 160px);
    overflow-y: auto;
    transition: box-shadow 0.3s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    // 滚动条样式优化
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #dcdfe6;
        border-radius: 3px;

        &:hover {
            background: #c0c4cc;
        }
    }
}

.tender-form {
    /deep/ .el-row {
        margin-bottom: 0;
    }

    .field-readonly {
        min-height: 40px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 0 12px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: #f8fafc;

        &__text {
            flex: 1;
            color: #606266;
            line-height: 20px;
            word-break: break-all;
        }
    }

    /deep/ .el-form-item {
        margin-bottom: 20px;
        text-align: start;

        .el-form-item__label {
            color: #606266;
            font-weight: 500;
            font-size: 14px;
        }

        .el-form-item__content {
            line-height: 40px;
        }
    }

    // 单选框组样式
    /deep/ .el-radio-group {
        .el-radio {
            margin-right: 24px;

            .el-radio__label {
                font-size: 14px;
                color: #606266;
                font-weight: 400;
            }

            &.is-checked {
                .el-radio__label {
                    color: #409EFF;
                }
            }
        }
    }

    // 复选框组样式
    /deep/ .el-checkbox-group {
        .el-checkbox {
            margin-right: 24px;
            margin-bottom: 12px;

            .el-checkbox__label {
                font-size: 14px;
                color: #606266;
                font-weight: 400;
            }

            &.is-checked {
                .el-checkbox__label {
                    color: #409EFF;
                }
            }
        }
    }

    // 输入框样式
    /deep/ .el-input {
        .el-input__inner {
            height: 40px;
            line-height: 40px;
            border-radius: 4px;
            border: 1px solid #dcdfe6;
            transition: all 0.3s ease;
            font-size: 14px;

            &:hover {
                border-color: #c0c4cc;
            }

            &:focus {
                border-color: #409EFF;
                outline: none;
            }

            &::placeholder {
                color: #c0c4cc;
            }
        }

        .el-input__suffix {
            .el-icon-search {
                color: #909399;
                transition: color 0.3s ease;

                &:hover {
                    color: #409EFF;
                }
            }
        }
    }

    // 级联选择器样式
    /deep/ .el-cascader {
        .el-input__inner {
            cursor: pointer;
        }
    }

    // 附件区域样式
    .attachment-section {
        .upload-tip {
            margin-bottom: 16px;
            // padding: 12px 16px;
            // background: #fef0f0;
            // border: 1px solid #fde2e2;
            // border-radius: 4px;
            text-align: left;

            span {
                color: #F56C6C;
                font-size: 12px;
            }
        }

        .attachment-type {
            margin-bottom: 16px;
        }

        .attachment-readonly {
            border: 1px solid #ebeef5;
            border-radius: 4px;
            background: #fff;
            overflow: hidden;

            &__header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: #f8fafc;
                border-bottom: 1px solid #ebeef5;
            }

            &__title {
                font-size: 14px;
                font-weight: 500;
                color: #303133;
            }
        }
    }

    // 操作按钮区域
    .form-actions {
        text-align: center;
        padding-top: 32px;
        margin-top: 32px;
        border-top: 1px solid #f0f0f0;

        /deep/ .el-form-item__content {
            display: flex;
            justify-content: center;
            gap: 16px;
        }

        /deep/ .el-button {
            min-width: 120px;
            height: 40px;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
            }

            &:active {
                transform: translateY(0);
            }
        }
    }
}

// 招标人管理弹窗样式
/deep/ .tenderer-dialog {
    .el-dialog {
        border-radius: 8px;
        overflow: hidden;
    }

    .el-dialog__header {
        background: #f5f7fa;
        padding: 20px 24px;
        border-bottom: 1px solid #e4e7ed;

        .el-dialog__title {
            font-size: 18px;
            font-weight: 600;
            color: #303133;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .tenderer-management {
        .search-area {
            background: #f5f7fa;
            padding: 16px;
            border-radius: 4px;
            margin-bottom: 16px;

            .el-form-item {
                margin-bottom: 0;
            }
        }

        .operation-buttons {
            margin-bottom: 16px;
        }

        .el-table {
            border-radius: 4px;
            overflow: hidden;

            th {
                background: #f5f7fa !important;
                color: #606266 !important;
                font-weight: 600 !important;
            }

            .el-button {
                &--mini {
                    padding: 5px 12px;
                    font-size: 12px;
                    border-radius: 3px;
                    transition: all 0.2s ease;

                    &:hover {
                        transform: translateY(-1px);
                    }
                }
            }
        }

        .pagination-wrapper {
            margin-top: 20px;
            text-align: right;
        }
    }
}

// 新增/编辑招标人弹窗样式
/deep/ .el-dialog {
    .dialog-footer {
        text-align: center;
        padding: 20px 0 10px;

        .el-button {
            min-width: 100px;
            transition: all 0.3s ease;

            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
            }
        }
    }
}

// 采购方案选择弹窗样式
/deep/ .purchase-scheme-dialog {
    .el-dialog {
        border-radius: 8px;
        overflow: hidden;
    }

    .el-dialog__header {
        background: #f5f7fa;
        padding: 20px 24px;
        border-bottom: 1px solid #e4e7ed;

        .el-dialog__title {
            font-size: 18px;
            font-weight: 600;
            color: #303133;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .purchase-scheme-management {
        .search-area {
            background: #f5f7fa;
            padding: 16px;
            border-radius: 4px;
            margin-bottom: 16px;

            .el-form-item {
                margin-bottom: 0;
            }
        }

        .el-table {
            border-radius: 4px;
            overflow: hidden;

            th {
                background: #f5f7fa !important;
                color: #606266 !important;
                font-weight: 600 !important;
            }

            .el-button {
                &--mini {
                    padding: 5px 12px;
                    font-size: 12px;
                    border-radius: 3px;
                    transition: all 0.2s ease;

                    &:hover {
                        transform: translateY(-1px);
                    }
                }
            }
        }

        .pagination-wrapper {
            margin-top: 20px;
            text-align: right;
        }
    }
}
</style>
